/**
 * 이상한 방 - 체인저(변화 생성기)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 5절(변화 규칙)·4.2절(무변화 차단) / 컨셉시트 4.2절
 *
 * ## 이 파일이 곧 게임의 재미다
 *
 * 수색 페이즈에서 술래가 상대하는 것은 방이 아니라 **여기서 만든 변화 12개**다.
 * 그래서 목표는 "변화를 12개 만드는 것"이 아니라 **찾을 만한 변화 12개**를 만드는 것이다.
 * 단순 랜덤은 두 방향으로 게임을 망친다:
 *  - 동사 쏠림 - 12개가 전부 색 변경이면 술래의 전략이 "색만 훑는다" 하나로 수렴한다
 *  - 공간 쏠림 - 12개가 거실에 몰리면 나머지 8구획이 장식으로 전락한다
 * 그래서 **동사·난이도·구획 세 축을 배분표로 먼저 정하고**, 그 배분을 만족하는 대상을 나중에 고른다.
 * 대상을 먼저 고르고 동사를 붙이는 순서였다면 배분은 결과일 뿐 목표가 되지 못한다.
 *
 * ## 검증은 복제하지 않는다
 *
 * 무변화 회전(4.2절)·동색 recolor·동일 셀 이동·셀 점유·경계는 전부 `verbs.ts`가 집행한다.
 * 이 파일은 **후보를 골라 verbs에 제안할 뿐**이고 거부당하면 다음 후보로 넘어간다.
 * 규칙을 여기 한 벌 더 적는 순간 두 벌이 어긋나며, 어긋난 쪽이 "찾을 수 없는 정답"을 만든다.
 *
 * ## 난이도 = 크기 랭크 + 변화량 랭크
 *
 * `score = sizeRank(0~2) + magnitudeRank(0~2)` → 3 이상 쉬움 / 1 이하 어려움 / 나머지 보통.
 * 소파를 5칸 옮기면 4점(쉬움), 머그컵을 1칸 옮기면 0점(어려움).
 * 이 한 줄이 4.1절("큰 물건일수록 1칸 이동이 안 보인다")을 역으로 이용한다 -
 * 원하는 난이도에서 크기가 정해지면 변화량이 자동으로 따라 나오고, 그래서 목표 곡선을 맞출 수 있다.
 *
 * ## 결정론
 *
 * `Math.random()`·`Date.now()` 없음. 같은 시드 + 같은 방 → 같은 변화 12개.
 * 방 생성과 다른 수열을 쓰기 위해 시드에 접미사를 붙인다(`rng.ts` 주석의 권장 방식).
 */

import {
  ROT_HALF_TURN,
  ROT_STEP_COUNT,
  VERBS,
  type CatalogEntry,
  type CatalogId,
  type Cell3,
  type InstanceId,
  type ObjectInstance,
  type PaletteIndex,
  type PlayerId,
  type RoomConfig,
  type RoomState,
  type Rot,
  type Verb,
} from '../core/types'
import { cellKey, rotatedFootprint } from '../core/grid'
import { compareCodePoint } from '../core/order'
import { buildOccupancy, type Catalog } from '../core/room'
import {
  applyAdd,
  applyMove,
  applyRecolor,
  applyRemove,
  applyRotate,
  changerVerbContext,
  type VerbContext,
} from '../core/verbs'
import { roomAt, type FloorPlan, type Room, type RoomId } from '../core/floorPlan'
import { createRng, type Rng } from '../core/rng'
import { PALETTE } from '../catalog/palette'
import { isFloorPlaceable } from '../catalog/placement'

/* --- 계약 타입 ------------------------------------------------------------------ */

/** 체인저의 동사. 코어 `Verb`와 같은 유니온이며 계약 문서의 이름에 맞춘 별칭이다. */
export type ChangeVerb = Verb

/** 술래 기준 체감 난이도. 배분 목표이자 리빌·계측의 라벨이다. */
export type ChangeDifficulty = 'easy' | 'normal' | 'hard'

/**
 * 변화 하나. `beforeCell`/`afterCell`은 **리빌 카메라가 볼 좌표**다.
 * 제거는 before만, 추가는 after만, 이동은 둘 다. 제자리 동사(회전·색)는 before만 채운다 -
 * 좌표가 바뀌지 않으므로 둘을 다 넣으면 같은 값이 두 벌 남을 뿐이다.
 *
 * `catalogId`·`zoneId`·`difficulty`는 선택 필드다. 판정에는 쓰이지 않지만
 * 리빌 라벨("거실의 소파")과 배분 계측이 이 값 없이는 카탈로그를 다시 뒤져야 한다.
 */
export type Change = Readonly<{
  id: string
  verb: ChangeVerb
  targetId: InstanceId
  beforeCell?: Cell3
  afterCell?: Cell3
  catalogId?: CatalogId
  zoneId?: RoomId
  difficulty?: ChangeDifficulty
}>

/**
 * 이 생성기가 실제로 읽는 튜닝 값만 추린 형태.
 * `game/tuning.ts`의 `TUNING`이 구조적으로 이 타입을 만족하므로 그대로 넘기면 된다.
 * 좁게 받는 이유는 의존을 줄이려는 것이 아니라 **테스트가 두 값만으로 A/B를 돌릴 수 있게** 하기 위해서다.
 */
export type ChangeGenTuning = Readonly<{
  changeCount: number
  allowedVerbs: readonly string[]
}>

export type ChangeGenResult = Readonly<{ mutated: RoomState; changes: readonly Change[] }>

/**
 * 생성 옵션. 지금은 하나뿐이다.
 *
 * `excludeIds` - **이미 건드린 것으로 칠** 인스턴스. 사람 체인저가 예산을 다 못 채우고 시간이 끝났을 때
 * 남은 개수만 이 생성기가 채우는데(매치 `matchPhases`), 그때 사람이 이미 손댄 물건을 다시 건드리면
 * 5.1절("한 물건은 한 번만")이 깨지고 판정 단위(`hunt.resolveShot`의 targetId → 변화 1:1)가 무너진다.
 * 후보에서 빼는 것이 아니라 `changedIds`에 **미리 넣는다** - 그러면 후보 정렬(`orderCandidates`)과
 * `verbs`의 `already-changed` 거부가 둘 다 같은 집합을 보고, 규칙이 한 벌로 남는다.
 * 생략하면 빈 집합이라 지금까지의 동작과 한 글자도 다르지 않다.
 */
export type GenerateChangesOptions = Readonly<{ excludeIds?: ReadonlySet<InstanceId> }>

/* --- 변화의 질 손잡이 ------------------------------------------------------------ */

/**
 * **변화의 질**을 결정하는 값들. 라운드 흐름 수치(탄환·시간·쿨타임)는 `tuning.ts`가 원본이고,
 * 여기 있는 것은 "생성된 12개가 어떤 모양인가"만 다룬다. 두 파일이 겹치지 않게 나눈 경계다.
 *
 * 전부 컨셉시트 7절의 "플레이테스트로만 답이 나오는" 부류이므로 export 한다 -
 * 테스트가 이 값을 다시 적으면 사본이 뒤처지고, 뒤처진 사본은 조정을 막는다.
 */
export const CHANGE_QUALITY = {
  /** 한 구획이 가져갈 수 있는 변화 수 상한. 12개가 한 방에 몰리는 것을 막는 유일한 장치다. */
  maxPerZone: 3,
  /** 변화가 퍼져야 하는 최소 구획 수. 테스트가 검사하는 하한이다. */
  minZones: 4,
  /** '제거' 상한. 최고 난도 동사라 많으면 라운드가 "빈 자리 찾기"로 수렴한다(설계문서 5.4 경고). */
  removeMax: 3,
  /** 동사 추첨 가중치. 이동·색이 높은 이유는 대상 선택의 자유도가 가장 크기 때문이다. */
  verbWeights: { move: 3, recolor: 3, rotate: 2, add: 2, remove: 2 },
  /** 난이도 목표 곡선(비율). 전부 어려우면 좌절하고 전부 쉬우면 시시하다. */
  difficultyMix: { easy: 4, normal: 5, hard: 3 },
  /** '추가'가 구획 태그를 일부러 어길 확률. 욕실의 냉장고는 버그로 읽히지만 가끔은 웃음이다. */
  offTagAddChance: 0.15,
  /**
   * **같은 소품 종류**가 한 라운드에서 가져갈 변화 수의 목표 상한.
   *
   * 동사 쏠림(`verbWeights`)과 공간 쏠림(`maxPerZone`)은 막고 있었는데 **소품 쏠림**은 막지 않고 있었다.
   * 실측(기본 시드 20판): 15판이 같은 카탈로그 항목을 2개 이상 건드렸고, 최악의 판(기본 시드
   * `weird-room-01`)은 12개 중 8개가 벽걸이 옷걸이 3 · 상부장 3 · 벽등 2였다. 술래에게 그 판은
   * "옷걸이 세 개를 찾는 판"이 되어 나머지 방이 통째로 장식으로 전락한다.
   *
   * 상한이 아니라 **선호**다(후보를 거르지 않고 순서만 바꾼다) - 거르면 개수 계약(12개)이 깨질 수 있고,
   * 방 구성에 따라서는 같은 소품을 두 번 건드리는 것이 유일한 선택지인 라운드가 실제로 존재한다.
   */
  maxPerCatalog: 1,
  /** 찾을 수 있다고 볼 최소 열린 방향 수(위 + 4방위 중). 아래 `isFindable` 주석 참고. */
  minOpenProbes: 2,
} as const

/* --- 내부 상수 ------------------------------------------------------------------ */

/** 방 생성과 수열을 분리하는 시드 접미사. 같이 쓰면 방 생성 호출 수가 바뀔 때 변화까지 흔들린다. */
const RNG_STREAM_SUFFIX = ':changes'
const CHANGE_ID_PREFIX = 'chg-'
const ADDED_ID_PREFIX = 'add-'
const ID_DIGITS = 3
/** 체인저가 놓은 것임을 표시한다. 서버 전용 필드라 클라이언트로 나가지 않는다(2.1절). */
const CHANGER: PlayerId = 'changer'

/** 배분표의 동사 순서 = 정본 `core/types.VERBS`. 순서가 결정론(같은 시드 → 같은 변화)에 들어가므로 여기서 리터럴을 다시 적지 않는다. */
const VERB_ORDER: readonly ChangeVerb[] = VERBS
// 회전 단계 수(`ROT_STEP_COUNT`)와 반회전(`ROT_HALF_TURN`)도 같은 이유로 `core/types` 하나를 가리킨다(P1 정리 - 사본 넷·둘을 걷었다).
const ROT_VALUES: readonly Rot[] = [0, 1, 2, 3]
/** 시도할 회전 증분. 0(무회전)은 verbs가 어차피 거부하므로 목록에 없다. 순서는 홀수 우선. */
const ROT_DELTAS: readonly number[] = [1, 3, 2]

/** 크기 랭크 경계(점유 셀 부피). 소파 96·침대 216이 큰 쪽, 머그 1·키보드 3이 작은 쪽에 든다. */
const BIG_VOLUME_CELLS = 60
const SMALL_VOLUME_CELLS = 12

const MAX_MAGNITUDE = 2
/** 난이도별 목표 점수. `sizeRank + magnitudeRank`가 이 값이 되도록 대상과 변화량을 고른다. */
const TARGET_SCORE: Readonly<Record<ChangeDifficulty, number>> = { easy: 3, normal: 2, hard: 1 }
const EASY_SCORE_MIN = 3
const HARD_SCORE_MAX = 1

/**
 * 변화량대별 이동 거리 후보(셀). 1셀 = `CELL_SIZE_METERS`(0.25m)다.
 *
 * **최소가 3셀(75cm)인 이유**는 측정이다. 예전 최소값은 1셀(25cm)이었는데,
 * 실제 생성물을 재 보니 그것이 전체 이동의 **12.9%**를 차지했다. 아이소메트릭 화면에서
 * 25cm는 눈에 들어오지 않아서 "옮겨졌다"가 사실상 찾을 수 없는 변화가 된다.
 * 개발자 지적: *"'옮겨졌다'가 캐치하기 너무 어려운거같은데, 옮기기는 최소 이동 셀을
 * 좀 넉넉하게 잡아서 눈에 보이게 해야할것같아"*
 *
 * 어려운 변화를 없애자는 것이 아니다 - **보이지 않는 변화를 없애자는 것**이다.
 * 관찰 게임에서 난이도는 "찾기 어렵다"여야지 "보이지 않는다"가 되면 그건 운이다.
 * 변화량 3단계는 그대로 유지되고, 대신 각 단계가 한 칸씩 위로 올라간다.
 */
const MOVE_DISTANCES_BY_MAGNITUDE: readonly (readonly number[])[] = [[3, 4], [5, 6], [8, 7, 9]]

/**
 * 실현된 거리를 변화량 랭크로 되돌리는 경계.
 * **위 표와 함께 움직여야 한다** - 거리는 올리고 경계를 두면 모든 이동이 최상위 랭크가 되어
 * 난이도 보고가 거짓말을 하게 된다(그러면 `summarizeChanges`의 쉬움/보통/어려움이 무너진다).
 */
const MOVE_FAR_CELLS = 7
const MOVE_MID_CELLS = 5
/** 8방위. 대각선을 포함해야 좁은 자리에서도 빠져나갈 방향이 남는다. */
const MOVE_DIRECTIONS: readonly Readonly<{ x: number; z: number }>[] = [
  { x: 1, z: 0 }, { x: -1, z: 0 }, { x: 0, z: 1 }, { x: 0, z: -1 },
  { x: 1, z: 1 }, { x: 1, z: -1 }, { x: -1, z: 1 }, { x: -1, z: -1 },
]

/** 색 변경의 변화량 경계(WCAG 상대 휘도 차). 팔레트 인접 두 색의 차가 0.02~0.23이다. */
const RECOLOR_FAR_LUMINANCE = 0.35
const RECOLOR_NEAR_LUMINANCE = 0.1
/** 난이도대마다 후보로 남길 색 수. 1이면 시드가 달라도 같은 색만 나온다. */
const RECOLOR_BAND_SIZE = 3

/** 제거의 변화량 랭크는 고정이다 - 없어진 것은 크기와 무관하게 "통째로" 바뀐 것이다. */
const REMOVE_MAGNITUDE = 1

/** 추가 위치의 중심도 경계(구획 반경 대비). 0.4 안쪽이면 한가운데, 0.75 밖이면 구석이다. */
const ADD_CENTER_RATIO = 0.4
const ADD_EDGE_RATIO = 0.75
const CENTER_HALF = 0.5
/** 두 갈래를 반반으로 가르는 확률. `rng.chance`가 확률을 요구하므로 이름을 따로 둔다. */
const COIN_FLIP = 0.5

const MAX_TARGET_SCANS = 24
const MAX_ADD_ENTRY_TRIES = 12
const MAX_ADD_SPOT_ATTEMPTS = 24

/* --- 카탈로그 구조적 읽기 --------------------------------------------------------- */

/**
 * 카탈로그 레이어가 코어 규격 위에 얹는 힌트. `roomGen.ts`와 같은 방식으로 **구조적으로** 읽는다 -
 * 이 생성기는 테스트 픽스처처럼 힌트가 없는 카탈로그로도 굴러가야 한다.
 */
type PlacementHints = CatalogEntry &
  Readonly<{ tags?: readonly string[]; recolorSlot?: unknown }>

function entryTags(entry: CatalogEntry): readonly string[] {
  return (entry as PlacementHints).tags ?? []
}

/*
 * 크기 등급(`sizeClass`)은 여기서 읽지 않는다 - "놓을 수 있는 소품인가"는 `catalog/placement.isFloorPlaceable` 하나가 답한다.
 * 사람 체인저의 도구(`input/changerLocks`)가 같은 판정을 필요로 하는데 `input/`은 이 파일을 볼 수 없어, 둘이 함께 보는
 * 카탈로그 층으로 올렸다(M4 리뷰).
 */

/**
 * 색을 바꿔도 화면이 바뀌는가. 카탈로그가 `recolorSlot: null`을 선언했다면
 * **칠할 슬롯이 없다**는 뜻이고, 그런 소품의 색 변경은 원칙 2가 금지하는 "찾을 수 없는 변화"다.
 * 필드 자체가 없는 카탈로그는 제약을 선언한 적이 없으므로 허용한다.
 */
function canRecolor(entry: CatalogEntry): boolean {
  return !('recolorSlot' in entry) || (entry as PlacementHints).recolorSlot !== null
}

/* --- 난이도 점수 ----------------------------------------------------------------- */

function volumeOf(entry: CatalogEntry): number {
  return entry.footprint.x * entry.footprint.y * entry.footprint.z
}

/** 0 = 작은 소품 / 1 = 중간 / 2 = 큰 가구. */
function sizeRank(entry: CatalogEntry): number {
  const volume = volumeOf(entry)
  if (volume >= BIG_VOLUME_CELLS) return 2
  if (volume <= SMALL_VOLUME_CELLS) return 0
  return 1
}

function scoreToDifficulty(score: number): ChangeDifficulty {
  if (score >= EASY_SCORE_MIN) return 'easy'
  if (score <= HARD_SCORE_MAX) return 'hard'
  return 'normal'
}

/**
 * 회전의 변화량. 90°/270°는 **점유 모양이 뒤바뀌어**(직사각 소품) 확 보이고,
 * 정사각 소품의 90°는 앞뒤만 달라져 중간, 180°는 실루엣만 뒤집혀 가장 미묘하다.
 */
function rotationMagnitude(delta: number, entry: CatalogEntry): number {
  if (delta % ROT_HALF_TURN === 0) return 0
  return entry.footprint.x === entry.footprint.z ? 1 : 2
}

/**
 * 이 대상에 이 동사로 **실제로 낼 수 있는** 변화량 목록.
 *
 * 구간이 아니라 목록인 것이 핵심이다. 회전은 **중간값을 건너뛴다** -
 * 직사각 소품은 90°(2)나 180°(0)뿐이고 1이 없다. 구간으로 다루면 "1도 낼 수 있다"고 거짓말을 하게 되고,
 * 그러면 보통을 노린 회전 슬롯이 매번 90°를 받아 쉬움으로 새어 나간다.
 * 그 반대 방향의 거짓말이 실측에서 회전만 어려움 26 : 보통 6으로 쏠리게 한 원인이었다.
 */
function achievableMagnitudes(entry: CatalogEntry, verb: ChangeVerb): readonly number[] {
  if (verb === 'remove') return [REMOVE_MAGNITUDE]
  if (verb === 'rotate') {
    const quarter = rotationMagnitude(1, entry)
    // rotSymmetry 2는 180°가 무변화라 verbs가 거부한다 → 90°만 남는다.
    return entry.rotSymmetry === 1 ? [0, quarter] : [quarter]
  }
  return [0, 1, MAX_MAGNITUDE]
}

/** 이 대상으로 원하는 난이도를 **정확히** 만들 수 있는가. */
function fitsDifficulty(entry: CatalogEntry, verb: ChangeVerb, targetScore: number): boolean {
  const rank = sizeRank(entry)
  return achievableMagnitudes(entry, verb).some((magnitude) => rank + magnitude === targetScore)
}

/** 목표 점수에서 얼마나 어긋나는가. 0이면 정확히 맞출 수 있다. */
function scoreGap(entry: CatalogEntry, verb: ChangeVerb, targetScore: number): number {
  const rank = sizeRank(entry)
  return Math.min(
    ...achievableMagnitudes(entry, verb).map((magnitude) => Math.abs(rank + magnitude - targetScore)),
  )
}

/** 목표 점수에 가장 가까운 변화량. 같은 거리면 **큰 쪽**을 고른다 - 애매하면 확실히 보이는 쪽이 낫다. */
function desiredMagnitude(entry: CatalogEntry, verb: ChangeVerb, targetScore: number): number {
  const wanted = targetScore - sizeRank(entry)
  let best = 0
  let bestGap = Number.POSITIVE_INFINITY

  for (const magnitude of achievableMagnitudes(entry, verb)) {
    const gap = Math.abs(magnitude - wanted)
    if (gap < bestGap || (gap === bestGap && magnitude > best)) {
      best = magnitude
      bestGap = gap
    }
  }
  return best
}

/* --- 세션 ----------------------------------------------------------------------- */

/**
 * 생성 도중의 작업 상태.
 * `occupancy`·`changedIds`는 **verbs에 넘긴 것과 같은 인스턴스**를 제자리 갱신한다 -
 * 사본을 넘기면 verbs가 낡은 점유 맵으로 검증하게 되고 그 순간 셀 충돌이 통과한다.
 * 게임 상태(`state`)만은 항상 verbs가 돌려준 새 객체로 교체한다(불변 규칙의 대상은 이것뿐이다).
 */
type Session = {
  readonly rng: Rng
  readonly catalog: Catalog
  readonly plan: FloorPlan
  readonly occupancy: Map<string, InstanceId>
  readonly changedIds: Set<InstanceId>
  readonly verbCtx: VerbContext
  /**
   * 구획 인덱스 → 변화 후보 인스턴스 id(셔플됨). 순서가 곧 선택 우선순위다.
   * 색인 자체가 세션(점유 맵·상태)을 읽어야 만들어지므로 생성 직후 한 번만 채운다.
   */
  zonePools: readonly (readonly InstanceId[])[]
  readonly zoneCounts: number[]
  /** 이번 라운드에서 소품 종류별로 이미 건드린 수. `maxPerCatalog` 선호의 입력이다. */
  readonly catalogCounts: Map<CatalogId, number>
  /** 바닥 y. 추가 동사가 소품을 놓는 높이이며 원본에서 역산한다(생성기 옵션을 몰라도 되게). */
  readonly floorY: number
  state: RoomState
  nextAddedSeq: number
}

function zoneRoom(session: Session, zoneIndex: number): Room | undefined {
  return session.plan.rooms[zoneIndex]
}

/* --- 기하 검사 ------------------------------------------------------------------- */

/**
 * 오브젝트가 **구획 내부에 온전히** 들어가는가.
 *
 * 방 rect는 벽·문을 제외한 내부 영역이므로(floorPlan 3절) 이 검사 하나가
 * 벽 뚫기·문 막기·구획 이탈을 동시에 막는다. verbs는 방 전체 경계와 셀 점유만 보므로
 * 이 검사가 없으면 소파가 벽 안으로 반쯤 들어가 "찾을 수 없는 변화"가 된다.
 */
function fitsZone(session: Session, room: Room, entry: CatalogEntry, cell: Cell3, rot: Rot): boolean {
  const size = rotatedFootprint(entry.footprint, rot)
  return (
    cell.x >= room.rect.x &&
    cell.x + size.x <= room.rect.x + room.rect.sizeX &&
    cell.z >= room.rect.z &&
    cell.z + size.z <= room.rect.z + room.rect.sizeZ &&
    cell.y >= 0 &&
    cell.y + size.y <= session.plan.bounds.sizeY
  )
}

/** 프로브 한 칸이 비어 있는가. 구획 밖(=벽)과 방 밖은 막힌 것으로 본다. */
function isProbeOpen(session: Session, room: Room, cell: Cell3, ignoredId?: InstanceId): boolean {
  if (cell.y < 0 || cell.y >= session.plan.bounds.sizeY) return false
  if (!(cell.x >= room.rect.x && cell.x < room.rect.x + room.rect.sizeX)) return false
  if (!(cell.z >= room.rect.z && cell.z < room.rect.z + room.rect.sizeZ)) return false
  const holder = session.occupancy.get(cellKey(cell))
  return holder === undefined || holder === ignoredId
}

/** 위 한 층 + 4방위 한 줄. 각 묶음은 "한 칸이라도 비면 그 방향은 트인 것"으로 판정한다. */
function probeGroups(cell: Cell3, size: Cell3): readonly (readonly Cell3[])[] {
  const top: Cell3[] = []
  const west: Cell3[] = []
  const east: Cell3[] = []
  const north: Cell3[] = []
  const south: Cell3[] = []

  for (let dx = 0; dx < size.x; dx += 1) {
    for (let dz = 0; dz < size.z; dz += 1) {
      top.push({ x: cell.x + dx, y: cell.y + size.y, z: cell.z + dz })
    }
    north.push({ x: cell.x + dx, y: cell.y, z: cell.z - 1 })
    south.push({ x: cell.x + dx, y: cell.y, z: cell.z + size.z })
  }
  for (let dz = 0; dz < size.z; dz += 1) {
    west.push({ x: cell.x - 1, y: cell.y, z: cell.z + dz })
    east.push({ x: cell.x + size.x, y: cell.y, z: cell.z + dz })
  }

  return [top, west, east, north, south]
}

/**
 * **찾을 수 있는 자리인가.** 아이소 카메라는 4방향으로 돌고 위에서 내려다보므로,
 * 위 또는 옆 중 두 방향 이상이 트여 있으면 어느 각도에서든 실루엣이 드러난다.
 *
 * 완전한 가시성 계산이 아니라 의도적으로 느슨한 근사다. 설계문서 3.1절이
 * "가려진 위치는 허용한다(소파 뒤·책상 밑은 정당한 난이도)"고 못박았으므로 여기서 걸러야 할 것은
 * **사방이 막힌 자리** 하나뿐이다. 더 조이면 난이도를 규칙으로 깎게 된다.
 */
function isFindable(
  session: Session,
  room: Room,
  entry: CatalogEntry,
  cell: Cell3,
  rot: Rot,
  ignoredId?: InstanceId,
): boolean {
  const size = rotatedFootprint(entry.footprint, rot)
  let open = 0
  for (const group of probeGroups(cell, size)) {
    if (group.some((probe) => isProbeOpen(session, room, probe, ignoredId))) open += 1
    if (open >= CHANGE_QUALITY.minOpenProbes) return true
  }
  return false
}

/* --- 배분표 --------------------------------------------------------------------- */

/** 알 수 없는 문자열은 조용히 버린다. 설정값은 외부(JSON·URL 파라미터)에서 올 수 있다. */
function resolveVerbs(allowed: readonly string[]): readonly ChangeVerb[] {
  const wanted = new Set(allowed)
  return VERB_ORDER.filter((verb) => wanted.has(verb))
}

/**
 * 동사별 상한. 고정값이 아니라 **허용 동사 수에 따라 늘어난다** -
 * 추가·제거만 허용한 A/B 모드(5.4절)에서 제거를 3개로 묶으면 나머지 9개가 전부 추가가 된다.
 * 상한의 목적은 쏠림 방지이지 특정 동사의 봉인이 아니다.
 */
function verbCap(verb: ChangeVerb, verbCount: number, changeCount: number): number {
  const evenShare = Math.ceil(changeCount / verbCount)
  const general = Math.max(evenShare + 1, 2)
  if (verb !== 'remove') return general
  return Math.min(general, Math.max(CHANGE_QUALITY.removeMax, evenShare))
}

/** 동사 배분표. 먼저 전 동사에 한 장씩 깔아 "12개가 전부 recolor"를 구조적으로 불가능하게 만든다. */
function planVerbBag(rng: Rng, verbs: readonly ChangeVerb[], count: number): readonly ChangeVerb[] {
  const bag: ChangeVerb[] = []
  const used = new Map<ChangeVerb, number>()
  const take = (verb: ChangeVerb): void => {
    bag.push(verb)
    used.set(verb, (used.get(verb) ?? 0) + 1)
  }

  for (const verb of verbs) {
    if (bag.length >= count) break
    take(verb)
  }

  while (bag.length < count) {
    const open = verbs.filter((verb) => (used.get(verb) ?? 0) < verbCap(verb, verbs.length, count))
    // 상한이 전부 찼는데 자리가 남았다면 개수 계약이 우선이다(상한은 품질 손잡이, 개수는 규칙).
    if (open.length === 0) {
      take(rng.pick(verbs))
      continue
    }
    take(rng.weighted(open, open.map((verb) => CHANGE_QUALITY.verbWeights[verb])))
  }

  return rng.shuffle(bag)
}

/** 난이도 배분표. 목표 곡선(쉬움 4 : 보통 5 : 어려움 3)을 개수에 맞춰 비례 배분한다. */
function planDifficultyBag(rng: Rng, count: number): readonly ChangeDifficulty[] {
  const mix = CHANGE_QUALITY.difficultyMix
  const total = mix.easy + mix.normal + mix.hard
  const easy = Math.round((count * mix.easy) / total)
  const hard = Math.round((count * mix.hard) / total)

  const bag: ChangeDifficulty[] = []
  for (let i = 0; i < easy && bag.length < count; i += 1) bag.push('easy')
  for (let i = 0; i < hard && bag.length < count; i += 1) bag.push('hard')
  while (bag.length < count) bag.push('normal')

  return rng.shuffle(bag)
}

/**
 * 구획 배분표. 후보가 있는 구획을 셔플한 뒤 **라운드 로빈**으로 한 장씩 돌린다.
 * 12개 / 9구획이면 전 구획이 최소 1개를 받고 세 구획만 2개가 된다 - 상한과 하한이 동시에 만족된다.
 */
function planZoneBag(rng: Rng, poolSizes: readonly number[], count: number): readonly number[] {
  const all = poolSizes.map((_, index) => index)
  const usable = all.filter((index) => (poolSizes[index] ?? 0) > 0)
  // 후보가 하나도 없어도 '추가' 동사는 성립하므로 전 구획을 대상으로 둔다.
  const order = rng.shuffle(usable.length > 0 ? usable : all)

  const bag: number[] = []
  const used = new Map<number, number>()
  for (let pass = 0; pass < CHANGE_QUALITY.maxPerZone && bag.length < count; pass += 1) {
    for (const zone of order) {
      if (bag.length >= count) break
      if ((used.get(zone) ?? 0) >= CHANGE_QUALITY.maxPerZone) continue
      used.set(zone, (used.get(zone) ?? 0) + 1)
      bag.push(zone)
    }
  }
  // 구획 수 × 상한보다 변화가 많은 설정이면 상한을 넘길 수밖에 없다. 개수 계약이 우선이다.
  while (bag.length < count) bag.push(order[bag.length % order.length] ?? 0)

  return rng.shuffle(bag)
}

/* --- 동사별 시도 ----------------------------------------------------------------- */

/** 시도 성공 결과. `magnitude`는 **실제로 실현된** 변화량이라 난이도 보고가 정직해진다. */
type Applied = Readonly<{
  state: RoomState
  magnitude: number
  beforeCell?: Cell3
  afterCell?: Cell3
}>

function distanceMagnitude(distance: number): number {
  if (distance >= MOVE_FAR_CELLS) return 2
  if (distance >= MOVE_MID_CELLS) return 1
  return 0
}

/**
 * 원하는 변화량대의 거리를 **전부** 시도한 뒤에야 아래 랭크로 내려간다. "못 옮김"보다 "덜 옮김"이 낫다.
 *
 * 랭크당 한 거리만 시도하고 실패하면 곧장 랭크가 내려가고, 그러면 "쉬움"을 노린 슬롯이 "보통"으로 흘러
 * 난이도 곡선이 통째로 어려운 쪽으로 밀린다(실측에서 실제로 그랬다). 밀도가 높은 방일수록 심해진다.
 * 그래서 랭크를 낮추기 전에 같은 랭크의 다른 거리(`MOVE_DISTANCES_BY_MAGNITUDE`의 한 행)를 먼저 다 시도한다.
 */
function moveDistances(magnitude: number): readonly number[] {
  const distances: number[] = []
  for (let rank = magnitude; rank >= 0; rank -= 1) distances.push(...(MOVE_DISTANCES_BY_MAGNITUDE[rank] ?? []))
  return distances
}

function tryMove(session: Session, room: Room, obj: ObjectInstance, entry: CatalogEntry, magnitude: number): Applied | null {
  for (const distance of moveDistances(magnitude)) {
    for (const dir of session.rng.shuffle(MOVE_DIRECTIONS)) {
      const cell: Cell3 = {
        x: obj.cell.x + dir.x * distance,
        y: obj.cell.y,
        z: obj.cell.z + dir.z * distance,
      }
      if (!fitsZone(session, room, entry, cell, obj.rot)) continue
      if (!isFindable(session, room, entry, cell, obj.rot, obj.id)) continue

      const result = applyMove(session.state, obj.id, cell, session.verbCtx)
      if (!result.ok) continue
      return {
        state: result.state,
        magnitude: distanceMagnitude(distance),
        beforeCell: obj.cell,
        afterCell: cell,
      }
    }
  }
  return null
}

/**
 * 원하는 변화량에 **가장 가까운 각도부터** 시도한다.
 * 예전에는 "변화량 2면 90°, 아니면 180°"로 갈랐는데, 그러면 변화량 1을 요구한 슬롯이
 * 항상 변화량 0인 180°를 받아 난이도가 한 칸씩 아래로 새어 나갔다.
 * 무변화 판정은 어느 각도든 verbs가 하므로 여기서는 순서만 정한다.
 */
function tryRotate(session: Session, room: Room, obj: ObjectInstance, entry: CatalogEntry, magnitude: number): Applied | null {
  const gap = (delta: number): number => Math.abs(rotationMagnitude(delta, entry) - magnitude)
  const deltas = [...ROT_DELTAS].sort((a, b) => gap(a) - gap(b))

  for (const delta of deltas) {
    const rot = ((obj.rot + delta) % ROT_STEP_COUNT) as Rot
    if (!fitsZone(session, room, entry, obj.cell, rot)) continue
    if (!isFindable(session, room, entry, obj.cell, rot, obj.id)) continue

    const result = applyRotate(session.state, obj.id, rot, session.verbCtx)
    if (!result.ok) continue
    return { state: result.state, magnitude: rotationMagnitude(delta, entry), beforeCell: obj.cell }
  }
  return null
}

function luminanceMagnitude(gap: number): number {
  if (gap >= RECOLOR_FAR_LUMINANCE) return 2
  if (gap >= RECOLOR_NEAR_LUMINANCE) return 1
  return 0
}

/**
 * 휘도차로 난이도를 만든다. 명도가 붙은 색끼리 바꾸면 어렵고 멀면 쉽다 -
 * 팔레트가 휘도 오름차순으로 정렬돼 있어(palette.ts) 이 계산이 인덱스 거리와 거의 같지만,
 * 인덱스가 아니라 **휘도**를 쓰는 이유는 팔레트에 색이 끼어들어도 난이도가 그대로 유지되기 때문이다.
 */
function tryRecolor(session: Session, obj: ObjectInstance, magnitude: number): Applied | null {
  const current = PALETTE[obj.colorId]
  if (current === undefined) return null

  const ranked = PALETTE.filter((color) => color.id !== obj.colorId)
    .map((color) => ({ id: color.id, gap: Math.abs(color.luminance - current.luminance) }))
    .sort((a, b) => a.gap - b.gap)

  for (const candidate of session.rng.shuffle(pickBand(ranked, magnitude))) {
    const result = applyRecolor(session.state, obj.id, candidate.id, session.verbCtx)
    if (!result.ok) continue
    return { state: result.state, magnitude: luminanceMagnitude(candidate.gap), beforeCell: obj.cell }
  }
  return null
}

/**
 * 변화량대에 **실제로 해당하는** 색만 남긴다. 순위로 구간을 자르면(가장 먼 3색 등)
 * 그중 일부가 임계값 아래라 원하는 난이도를 못 내는데, 그 미끄러짐이 곡선 전체를 어려운 쪽으로 민다.
 * 그 대에 색이 하나도 없을 때만 순위 기준으로 물러난다.
 */
function pickBand(
  ranked: readonly Readonly<{ id: PaletteIndex; gap: number }>[],
  magnitude: number,
): readonly Readonly<{ id: PaletteIndex; gap: number }>[] {
  const exact = ranked.filter((candidate) => luminanceMagnitude(candidate.gap) === magnitude)
  if (exact.length > 0) return exact

  const size = Math.min(RECOLOR_BAND_SIZE, ranked.length)
  if (magnitude <= 0) return ranked.slice(0, size)
  if (magnitude >= MAX_MAGNITUDE) return ranked.slice(ranked.length - size)
  const start = Math.max(0, Math.floor((ranked.length - size) / 2))
  return ranked.slice(start, start + size)
}

function tryRemove(session: Session, obj: ObjectInstance): Applied | null {
  const result = applyRemove(session.state, obj.id, session.verbCtx)
  if (!result.ok) return null
  return { state: result.state, magnitude: REMOVE_MAGNITUDE, beforeCell: obj.cell }
}

/** 구획 중심에서 얼마나 떨어졌는가. 2 = 한가운데(눈에 띔) / 0 = 구석(잘 안 보임). */
function centralityRank(room: Room, cell: Cell3, size: Cell3): number {
  const centerX = room.rect.x + room.rect.sizeX * CENTER_HALF
  const centerZ = room.rect.z + room.rect.sizeZ * CENTER_HALF
  const offsetX = Math.abs(cell.x + size.x * CENTER_HALF - centerX) / (room.rect.sizeX * CENTER_HALF)
  const offsetZ = Math.abs(cell.z + size.z * CENTER_HALF - centerZ) / (room.rect.sizeZ * CENTER_HALF)
  const distance = Math.max(offsetX, offsetZ)

  if (distance <= ADD_CENTER_RATIO) return 2
  if (distance <= ADD_EDGE_RATIO) return 1
  return 0
}

/** 중심도 목표에 맞춰 좌표를 뽑는다. 구석을 노릴 때는 rect 가장자리에 붙인다. */
function sampleAddCell(session: Session, room: Room, size: Cell3, magnitude: number): Cell3 {
  const maxX = room.rect.x + room.rect.sizeX - size.x
  const maxZ = room.rect.z + room.rect.sizeZ - size.z
  const rng = session.rng

  if (magnitude >= MAX_MAGNITUDE) {
    const insetX = Math.floor(room.rect.sizeX * ADD_CENTER_RATIO * CENTER_HALF)
    const insetZ = Math.floor(room.rect.sizeZ * ADD_CENTER_RATIO * CENTER_HALF)
    const centerX = room.rect.x + Math.floor((room.rect.sizeX - size.x) * CENTER_HALF)
    const centerZ = room.rect.z + Math.floor((room.rect.sizeZ - size.z) * CENTER_HALF)
    return {
      x: Math.min(maxX, Math.max(room.rect.x, centerX + rng.int(-insetX, insetX))),
      y: session.floorY,
      z: Math.min(maxZ, Math.max(room.rect.z, centerZ + rng.int(-insetZ, insetZ))),
    }
  }

  if (magnitude <= 0) {
    // 네 벽 중 하나에 붙인다. 나머지 축만 자유롭게 흩뿌린다.
    const alongX = rng.chance(COIN_FLIP)
    const flush = rng.chance(COIN_FLIP)
    if (alongX) {
      return { x: rng.int(room.rect.x, maxX), y: session.floorY, z: flush ? room.rect.z : maxZ }
    }
    return { x: flush ? room.rect.x : maxX, y: session.floorY, z: rng.int(room.rect.z, maxZ) }
  }

  return { x: rng.int(room.rect.x, maxX), y: session.floorY, z: rng.int(room.rect.z, maxZ) }
}

/**
 * '추가'가 쓸 소품 후보. **구획 태그를 존중한다** - 욕실의 냉장고는 난이도가 아니라 버그로 읽힌다.
 * 다만 낮은 확률로 일부러 어긋난 것을 섞는다(컨셉시트 5절의 뻘짓 자유도).
 * 벽면물·천장물은 제외한다(`isFloorPlaceable`). 바닥에 눕힌 거울은 변화가 아니라 렌더 사고로 보인다.
 */
function addCandidates(session: Session, room: Room, targetScore: number, offTag: boolean, strict: boolean): readonly CatalogEntry[] {
  const usable = [...session.catalog.values()]
    .filter(isFloorPlaceable)
    .sort((a, b) => compareCodePoint(a.id, b.id))

  const tagged = offTag ? usable : usable.filter((entry) => {
    const tags = entryTags(entry)
    return tags.length === 0 || tags.includes(room.tag)
  })
  // 이번 라운드에서 이미 건드린 소품 종류는 뒤로 민다(안정 정렬이라 나머지는 셔플 순서를 지킨다).
  const pool = session.rng
    .shuffle(tagged.length > 0 ? tagged : usable)
    .sort((a, b) => catalogRepeat(session, a.id) - catalogRepeat(session, b.id))
  const fitting = pool.filter((entry) => fitsDifficulty(entry, 'add', targetScore))
  if (strict) return fitting

  const chosen = new Set(fitting.map((entry) => entry.id))
  return [...fitting, ...pool.filter((entry) => !chosen.has(entry.id))]
}

function nextAddedId(session: Session): InstanceId {
  let id: InstanceId
  do {
    session.nextAddedSeq += 1
    id = `${ADDED_ID_PREFIX}${String(session.nextAddedSeq).padStart(ID_DIGITS, '0')}`
  } while (session.state.objects.has(id))
  return id
}

function tryAdd(session: Session, room: Room, targetScore: number, strict: boolean): (Applied & { targetId: InstanceId; entry: CatalogEntry }) | null {
  const offTag = session.rng.chance(CHANGE_QUALITY.offTagAddChance)

  for (const entry of addCandidates(session, room, targetScore, offTag, strict).slice(0, MAX_ADD_ENTRY_TRIES)) {
    const magnitude = desiredMagnitude(entry, 'add', targetScore)

    for (let attempt = 0; attempt < MAX_ADD_SPOT_ATTEMPTS; attempt += 1) {
      const rot = session.rng.pick(ROT_VALUES)
      const size = rotatedFootprint(entry.footprint, rot)
      // 이 회전으로는 구획에 들어가지 않는다. 다른 회전이면 x/z가 바뀌어 들어갈 수 있으므로 다음 시도로.
      if (size.x > room.rect.sizeX || size.z > room.rect.sizeZ) continue

      const cell = sampleAddCell(session, room, size, magnitude)
      if (!fitsZone(session, room, entry, cell, rot)) continue
      if (!isFindable(session, room, entry, cell, rot)) continue

      const obj: ObjectInstance = {
        id: nextAddedId(session),
        catalogId: entry.id,
        cell,
        rot,
        colorId: entry.defaultColorId,
        placedBy: CHANGER,
      }
      const result = applyAdd(session.state, obj, session.verbCtx)
      if (!result.ok) continue

      return {
        state: result.state,
        magnitude: centralityRank(room, cell, size),
        afterCell: cell,
        targetId: obj.id,
        entry,
      }
    }
  }
  return null
}

/* --- 슬롯 채우기 ----------------------------------------------------------------- */

function isEligible(entry: CatalogEntry, verb: ChangeVerb): boolean {
  if (verb === 'rotate') return entry.rotSymmetry !== 4
  if (verb === 'recolor') return canRecolor(entry)
  return verb === 'move' || verb === 'remove'
}

/**
 * 변화 하나를 확정하고 파생 인덱스를 갱신한다.
 *
 * 점유 맵은 델타로 고치지 않고 **통째로 다시 만든다.** 한 라운드에 12번뿐이라 비용이 무의미하고,
 * `buildOccupancy`가 셀 점유 규칙 위반을 그 자리에서 던져 주므로 매 변화가 곧 불변식 검사가 된다.
 * 델타 갱신은 빠르지만, 한 번 어긋나면 그 뒤 모든 검증이 조용히 거짓이 된다.
 */
function commit(
  session: Session,
  zoneIndex: number,
  targetId: InstanceId,
  catalogId: CatalogId,
  applied: Applied,
): void {
  session.state = applied.state
  session.changedIds.add(targetId)
  session.zoneCounts[zoneIndex] = (session.zoneCounts[zoneIndex] ?? 0) + 1
  // 제거는 오브젝트가 새 상태에서 사라지므로 여기서 되찾을 수 없다. 그래서 호출부가 넘긴다.
  session.catalogCounts.set(catalogId, (session.catalogCounts.get(catalogId) ?? 0) + 1)

  session.occupancy.clear()
  for (const [key, id] of buildOccupancy(session.state, session.catalog)) session.occupancy.set(key, id)
}

function changeId(slotIndex: number): string {
  return `${CHANGE_ID_PREFIX}${String(slotIndex + 1).padStart(ID_DIGITS, '0')}`
}

/**
 * 후보 정렬. `strict`면 **원하는 난이도를 낼 수 있는 대상만** 남긴다.
 *
 * 이 스위치가 곡선을 지킨다. 예전에는 항상 "맞는 것 먼저, 없으면 아무거나"였는데,
 * 그러면 첫 구획이 언제나 아무거나로 성공해 버려 다른 구획을 볼 기회 자체가 사라졌다 -
 * 큰 가구가 없는 구획에 "쉬움" 슬롯이 배정되면 그 슬롯은 그 자리에서 보통으로 주저앉았다.
 * 풀 자체가 이미 셔플돼 있어 여기서는 추가 난수를 쓰지 않는다.
 */
function orderCandidates(
  session: Session,
  pool: readonly InstanceId[],
  verb: ChangeVerb,
  targetScore: number,
  strict: boolean,
): readonly InstanceId[] {
  const fitting: { id: InstanceId; repeat: number }[] = []
  const rest: { id: InstanceId; gap: number; repeat: number }[] = []

  for (const id of pool) {
    if (session.changedIds.has(id)) continue
    const obj = session.state.objects.get(id)
    if (obj === undefined) continue
    const entry = session.catalog.get(obj.catalogId)
    if (entry === undefined || !isEligible(entry, verb)) continue
    const repeat = catalogRepeat(session, obj.catalogId)
    if (fitsDifficulty(entry, verb, targetScore)) fitting.push({ id, repeat })
    else if (!strict) rest.push({ id, gap: scoreGap(entry, verb, targetScore), repeat })
  }

  // 이미 건드린 소품 종류는 뒤로 민다(버리지는 않는다 - `maxPerCatalog` 주석).
  fitting.sort((a, b) => a.repeat - b.repeat)
  // 목표를 못 맞출 때도 **가장 덜 어긋나는 쪽**으로 미끄러진다. 정렬이 안정적이라 동점은 셔플 순서를 지킨다.
  // 난이도가 소품 중복보다 우선이다 - 중복은 지루함이지만 난이도 붕괴는 라운드의 실패다.
  rest.sort((a, b) => a.gap - b.gap || a.repeat - b.repeat)
  return [...fitting.map((c) => c.id), ...rest.map((candidate) => candidate.id)]
}

/** 이 소품 종류가 목표 상한을 얼마나 넘었는가. 0이면 아직 여유가 있다(선호 대상). */
function catalogRepeat(session: Session, catalogId: CatalogId): number {
  const used = session.catalogCounts.get(catalogId) ?? 0
  return Math.max(0, used - CHANGE_QUALITY.maxPerCatalog + 1)
}

function tryVerbInZone(session: Session, verb: ChangeVerb, zoneIndex: number, difficulty: ChangeDifficulty, slotIndex: number, strict: boolean): Change | null {
  const room = zoneRoom(session, zoneIndex)
  if (room === undefined) return null
  const targetScore = TARGET_SCORE[difficulty]

  if (verb === 'add') {
    const added = tryAdd(session, room, targetScore, strict)
    if (added === null) return null

    const realized = scoreToDifficulty(sizeRank(added.entry) + added.magnitude)
    if (strict && realized !== difficulty) return null

    commit(session, zoneIndex, added.targetId, added.entry.id, added)
    return {
      id: changeId(slotIndex),
      verb,
      targetId: added.targetId,
      afterCell: added.afterCell,
      catalogId: added.entry.id,
      zoneId: room.id,
      difficulty: realized,
    }
  }

  const pool = session.zonePools[zoneIndex] ?? []
  for (const id of orderCandidates(session, pool, verb, targetScore, strict).slice(0, MAX_TARGET_SCANS)) {
    const obj = session.state.objects.get(id)
    const entry = obj === undefined ? undefined : session.catalog.get(obj.catalogId)
    if (obj === undefined || entry === undefined) continue

    const magnitude = desiredMagnitude(entry, verb, targetScore)
    const applied = attemptVerb(session, room, verb, obj, entry, magnitude)
    if (applied === null) continue

    // 요구한 변화량이 늘 실현되지는 않는다 - 90° 회전이 막혀 180°로 대체되는 식이다.
    // 엄격 모드에서는 그 결과를 **버린다**. verbs가 순수 함수라 아직 `session.state`에 반영되지 않았고,
    // 버리는 비용이 0이다. 곡선을 지키는 마지막 관문이자 가장 싼 관문이다.
    const realized = scoreToDifficulty(sizeRank(entry) + applied.magnitude)
    if (strict && realized !== difficulty) continue

    commit(session, zoneIndex, obj.id, entry.id, applied)
    return {
      id: changeId(slotIndex),
      verb,
      targetId: obj.id,
      beforeCell: applied.beforeCell,
      afterCell: applied.afterCell,
      catalogId: entry.id,
      zoneId: room.id,
      difficulty: realized,
    }
  }
  return null
}

function attemptVerb(session: Session, room: Room, verb: ChangeVerb, obj: ObjectInstance, entry: CatalogEntry, magnitude: number): Applied | null {
  switch (verb) {
    case 'move':
      return tryMove(session, room, obj, entry, magnitude)
    case 'rotate':
      return tryRotate(session, room, obj, entry, magnitude)
    case 'recolor':
      return tryRecolor(session, obj, magnitude)
    case 'remove':
      return tryRemove(session, obj)
    case 'add':
      return null
  }
}

/**
 * 한 슬롯을 채운다. 사다리 순서가 곧 우선순위이며, **난이도를 구획보다 먼저 포기하지 않는다**:
 * 배정 구획(난이도 엄격) → 다른 구획(엄격) → 배정 구획(완화) → 다른 구획(완화) → 아무 동사.
 * 배분표는 품질 목표이고 **개수는 계약**이므로, 마지막 칸에서는 배분을 포기하고 개수를 지킨다.
 */
function commitSlot(session: Session, verbs: readonly ChangeVerb[], verb: ChangeVerb, difficulty: ChangeDifficulty, zoneIndex: number, slotIndex: number): Change {
  const allZones = session.plan.rooms.map((_, index) => index)
  const underCap = session.rng.shuffle(
    allZones.filter((index) => (session.zoneCounts[index] ?? 0) < CHANGE_QUALITY.maxPerZone),
  )
  const anyZone = session.rng.shuffle(allZones)
  const ladder: readonly Readonly<{ zones: readonly number[]; strict: boolean }>[] = [
    { zones: [zoneIndex], strict: true },
    { zones: underCap, strict: true },
    { zones: [zoneIndex], strict: false },
    { zones: underCap, strict: false },
    { zones: anyZone, strict: false },
  ]

  for (const step of ladder) {
    for (const zone of step.zones) {
      const change = tryVerbInZone(session, verb, zone, difficulty, slotIndex, step.strict)
      if (change !== null) return change
    }
  }

  for (const alternative of session.rng.shuffle(verbs)) {
    if (alternative === verb) continue
    for (const zone of anyZone) {
      const change = tryVerbInZone(session, alternative, zone, difficulty, slotIndex, false)
      if (change !== null) return change
    }
  }

  throw new RangeError(
    `generateChanges: ${slotIndex + 1}번째 변화를 만들 수 없다 - ` +
      `방이 너무 작거나 후보가 고갈됐다(오브젝트 ${session.state.objects.size}개, 허용 동사 ${verbs.join('/')})`,
  )
}

/* --- 진입점 --------------------------------------------------------------------- */

/** 후보 색인. 구획 밖(평면도 결함)·카탈로그 미등록·사방이 막힌 오브젝트는 애초에 제외한다. */
function buildZonePools(session: Session): readonly (readonly InstanceId[])[] {
  const pools: InstanceId[][] = session.plan.rooms.map(() => [])
  const sorted = [...session.state.objects.values()].sort((a, b) => compareCodePoint(a.id, b.id))

  for (const obj of sorted) {
    const entry = session.catalog.get(obj.catalogId)
    if (entry === undefined) continue
    const room = roomAt(session.plan, obj.cell.x, obj.cell.z)
    if (room === undefined) continue
    const zoneIndex = session.plan.rooms.indexOf(room)
    if (!isFindable(session, room, entry, obj.cell, obj.rot, obj.id)) continue
    pools[zoneIndex]?.push(obj.id)
  }

  return pools.map((pool) => session.rng.shuffle(pool))
}

/** 바닥 y. 원본에서 가장 낮은 오브젝트의 높이를 바닥으로 본다(생성기 옵션을 몰라도 되게). */
function detectFloorY(state: RoomState): number {
  let floorY = 0
  let first = true
  for (const obj of state.objects.values()) {
    if (first || obj.cell.y < floorY) floorY = obj.cell.y
    first = false
  }
  return floorY
}

/**
 * 시드로부터 변화를 생성한다. **같은 시드 + 같은 원본 → 같은 변화 집합.**
 *
 * 반환하는 `mutated`는 원본을 건드리지 않은 새 상태다(2절: 두 스냅샷이 판정의 진실).
 * 개수를 채우지 못하면 조용히 적게 돌려주지 않고 **던진다** - HUD가 "0/12"를 띄우는데
 * 실제 정답이 5개뿐인 라운드는 버그가 아니라 사기이기 때문이다.
 */
export function generateChanges(
  original: RoomState,
  seed: number | string,
  tuning: ChangeGenTuning,
  catalog: Catalog,
  plan: FloorPlan,
  options: GenerateChangesOptions = {},
): ChangeGenResult {
  const verbs = resolveVerbs(tuning.allowedVerbs)
  if (verbs.length === 0) {
    throw new RangeError(`generateChanges: 허용된 동사가 없다 (${tuning.allowedVerbs.join('/')})`)
  }
  if (!Number.isInteger(tuning.changeCount) || tuning.changeCount < 0) {
    throw new RangeError(`generateChanges: changeCount는 0 이상의 정수여야 한다: ${tuning.changeCount}`)
  }
  if (plan.rooms.length === 0) {
    throw new RangeError('generateChanges: 구획이 하나도 없는 평면도에는 변화를 만들 수 없다')
  }
  if (tuning.changeCount === 0) return { mutated: original, changes: [] }

  const occupancy = new Map(buildOccupancy(original, catalog))
  // 사람 체인저가 먼저 건드린 것은 이 생성기에게도 "이미 건드린 것"이다(`GenerateChangesOptions` 주석).
  const changedIds = new Set<InstanceId>(options.excludeIds ?? [])
  const config: RoomConfig = {
    allowedVerbs: new Set(verbs),
    changeCount: tuning.changeCount,
    placementsPerPlayer: 0,
  }

  const session: Session = {
    rng: createRng(`${String(seed)}${RNG_STREAM_SUFFIX}`),
    catalog,
    plan,
    occupancy,
    changedIds,
    // 스위치 집합(한 물건은 한 번만 5.1절 · 옮김은 높이 유지 · 인당 배치 수 제한 없음)은 사람 체인저의 호스트·도구와 **같은 함수**에서
    // 받는다 - 생성기가 리터럴로 한 벌 더 들면 스위치가 하나 늘 때마다 두 자리가 갈린다(P1에서 그렇게 갈린 것을 걷었다).
    // `tryMove`는 원래 `y: obj.cell.y`로만 후보를 만들어 높이 유지 스위치에 걸리는 후보가 없다 - 후보 생성이 바뀌는 날에도 verbs가 막는다.
    verbCtx: {
      ...changerVerbContext(catalog, config, plan.bounds, changedIds),
      // 같은 인스턴스를 넘긴다 - 사본이면 verbs가 낡은 점유 맵으로 검증한다. `changerVerbContext`는 점유 맵을 모른다(호스트는 매번 세운다).
      occupancy,
    },
    zonePools: [],
    zoneCounts: plan.rooms.map(() => 0),
    catalogCounts: new Map<CatalogId, number>(),
    floorY: detectFloorY(original),
    state: original,
    nextAddedSeq: 0,
  }
  session.zonePools = buildZonePools(session)

  const poolSizes = session.zonePools.map((pool) => pool.length)
  const verbBag = planVerbBag(session.rng, verbs, tuning.changeCount)
  const difficultyBag = planDifficultyBag(session.rng, tuning.changeCount)
  const zoneBag = planZoneBag(session.rng, poolSizes, tuning.changeCount)

  const changes: Change[] = []
  for (let slot = 0; slot < tuning.changeCount; slot += 1) {
    const verb = verbBag[slot] ?? verbs[0]
    const difficulty = difficultyBag[slot] ?? 'normal'
    const zone = zoneBag[slot] ?? 0
    // 배분표는 항상 changeCount 길이라 도달할 수 없다. 그래도 조용히 빠져나가지는 않는다 -
    // 여기서 break 하면 개수 계약이 소리 없이 깨진 라운드가 그대로 화면에 나간다.
    if (verb === undefined) throw new RangeError('generateChanges: 동사 배분표가 비었다')
    changes.push(commitSlot(session, verbs, verb, difficulty, zone, slot))
  }

  return { mutated: session.state, changes }
}

/* --- 계측 ----------------------------------------------------------------------- */

export type ChangeSummary = Readonly<{
  byVerb: Readonly<Record<string, number>>
  byDifficulty: Readonly<Record<string, number>>
  byZone: Readonly<Record<string, number>>
  zoneCount: number
}>

/**
 * 배분 결과를 센다. 테스트와 튜닝 HUD가 같은 함수를 쓰게 하려는 것 -
 * 세는 방법이 두 벌이면 "테스트는 통과하는데 화면 숫자는 다른" 상황이 생긴다.
 */
export function summarizeChanges(changes: readonly Change[]): ChangeSummary {
  const byVerb: Record<string, number> = {}
  const byDifficulty: Record<string, number> = {}
  const byZone: Record<string, number> = {}

  for (const change of changes) {
    byVerb[change.verb] = (byVerb[change.verb] ?? 0) + 1
    const difficulty = change.difficulty ?? 'normal'
    byDifficulty[difficulty] = (byDifficulty[difficulty] ?? 0) + 1
    const zone = change.zoneId ?? 'unknown'
    byZone[zone] = (byZone[zone] ?? 0) + 1
  }

  return { byVerb, byDifficulty, byZone, zoneCount: Object.keys(byZone).length }
}
